import { ColumnDef, DeepKeys } from "@tanstack/react-table";

import {
  AssignedBy,
  ProductAssignment,
  ProductLogsItem,
} from "@/features/tools/interfaces/tools.interfaces";
import type { PermissionData } from "@/features/workforce";
import type { Workspace } from "@/features/workspace/workspace.interfaces";

export enum EmploymentType {
  "Full Time" = "FULLTIME",
  "Part Time" = "PARTTIME",
  "Casual" = "CASUAL",
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Any = any;

export type Geo = {
  latitude?: string;
  longitude?: string;
  label?: string;
};

export enum EmployeeStatus {
  ACTIVE = "ACTIVE",
  DEACTIVATED = "DEACTIVATED",
  TERMINATED = "TERMINATED",
}

export enum UserStatus {
  ACTIVE = "ACTIVE",
  DEACTIVATED = "DEACTIVATED",
  TERMINATED = "TERMINATED",
}

export type Status =
  | EmployeeStatus.ACTIVE
  | EmployeeStatus.DEACTIVATED
  | EmployeeStatus.TERMINATED;

export type Role =
  | "SUPERADMIN"
  | "ADMIN"
  | "MANAGER"
  | "EMPLOYEE"
  | "CUSTOMER"
  | "CUSTOMERUSER"
  | "ACCOUNT";

export enum RoleEnum {
  SUPER_ADMIN = "SUPER_ADMIN",
  ADMIN = "ADMIN",
  MANAGER = "MANAGER",
  EMPLOYEE = "EMPLOYEE",
  CUSTOMER = "CUSTOMER",
  CUSTOMERUSER = "CUSTOMERUSER",
  ACCOUNT = "ACCOUNT",
}

export interface WorkForceUserEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
  email?: string;
  phoneNumber?: string;
  status?: Status;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  address?: string;
  image?: string;
  deviceId?: string;
  permissions?: PermissionData;
  role?: Role;
  employmentType?: EmploymentType;
  tabAccess: boolean;
}

export interface UserAccessList {
  id: string;
  userId?: string;
  workspace?: Pick<Workspace, "id" | "name" | "logo" | "subDomain">[];
}

export enum Permissions {
  ALL = "ALL",
  DASHBOARD = "DASHBOARD",
  ROSTER = "ROSTER",
  PROJECT_REGISTER = "PROJECT_REGISTER",
  ESTIMATES = "ESTIMATES",
  CLAIMS = "CLAIMS",
  PROJECTS = "PROJECTS",
  PROJECTS_PROJECT = "PROJECTS_PROJECT",
  WORKFORCE = "WORKFORCE",
  WORKFORCE_ADMIN = "WORKFORCE_ADMIN",
  WORKFORCE_MANAGER = "WORKFORCE_MANAGER",
  WORKFORCE_EMPLOYEE = "WORKFORCE_EMPLOYEE",
  WORKFORCE_SUBCONTRACTOR = "WORKFORCE_SUBCONTRACTOR",
  WORKFORCE_SETTINGS = "WORKFORCE_SETTING",
  CUSTOMER = "CUSTOMER",
  CUSTOMER_CUSTOMER = "CUSTOMER_CUSTOMER",
  CUSTOMER_PROSPECT = "CUSTOMER_PROSPECT",
  WASTEMANAGE = "WASTEMANAGE",
  WASTEMANAGE_SUMMARY = "WASTEMANAGE_SUMMARY",
  WASTEMANAGE_REQUEST = "WASTEMANAGE_REQUEST",
  WASTEMANAGE_WEIGHTSCALE = "WASTEMANAGE_WEIGHTSCALE",
  WASTEMANAGE_WEIGHTINGREPORTS = "WASTEMANAGE_WEIGHTINGREPORTS",
  WASTEMANAGE_WEIGHBRIDGE = "WASTEMANAGE_WEIGHBRIDGE",
  WASTEMANAGE_VEHICLES = "WASTEMANAGE_VEHICLES",
  WASTEMANAGE_LOADTYPE = "WASTEMANAGE_LOADTYPE",
  WASTEMANAGE_TIPPING_YARD = "WASTEMANAGE_TIPPING_YARD",
  WASTEMANAGE_WASTETYPE = "WASTEMANAGE_WASTETYPE",
  WASTEMANAGE_DAILYREPORTS = "WASTEMANAGE_DAILYREPORTS",
  WASTEMANAGE_SETTINGS = "WASTEMANAGE_SETTINGS",
  REPORTS = "REPORTS",
  FLEETS = "FLEETS",
  REPORTS_PROJECT_DAILYREPORT = "REPORTS_PROJECT_DAILYREPORT",
  DAILY_REPORT = "REPORTS_DAILY",
  REPORTS_WORKDOCKET = "REPORTS_WORKDOCKET",
  REPORTS_TIPPING = "REPORTS_TIPPING",
  REPORTS_REPORT_QUESTION = "REPORTS_REPORT_QUESTION",
  REPORTS_REPORT_SETTINGS = "REPORTS_REPORT_SETTINGS",
  REPORTS_WORKDOCKET_EMPLOYEE = "REPORTS_WORKDOCKET_EMPLOYEE",
  INVOICE = "INVOICE",
  INVOICE_DOCKET = "INVOICE_DOCKET",
  INVOICE_CONTRACT = "INVOICE_CONTRACT",
  INVOICE_XERO = "INVOICE_XERO",
  WAREHOUSE = "WAREHOUSE",
  TIMETRACKING = "TIMETRACKING",
  TIMETRACKING_SUMMARY = "TIMETRACKING_SUMMARY",
  TIMETRACKING_TIMESHEET = "TIMETRACKING_TIMESHEET",
  TIMETRACKING_SUBCONTRACTOR = "TIMETRACKING_SUBCONTRACTOR",
  TIMETRACKING_WORKLOGS = "TIMETRACKING_WORKLOGS",
  TIMETRACKING_SETTINGS = "TIMETRACKING_SETTINGS",
  PAYROLL = "PAYROLL",
  PAYROLL_CASE2 = "PAYROLL_CASE2",
  PAYROLL_PART1 = "PAYROLL_PART1",
  ITEMS = "ITEMS",
  CALENDAR = "CALENDAR",
  TOOLS = "TOOLS",
  MESSAGES = "MESSAGES",
  TEMPLATE = "TEMPLATE",
  TASKS = "TASKS",
  OHS = "OHS",
  SUPPORT = "SUPPORT",
  ANNOUNCEMENT = "ANNOUNCEMENT",
  SETTING = "SETTING",
  PROJECTDIRECTORY = "PROJECTDIRECTORY",
  DOCUMENT_PARSER = "DOCUMENT_PARSER",
  ACTIVITY = "ACTIVITY",
  INTELLIFLOW = "INTELLIFLOW",
  CUSTOMER_PORTAL = "CLIENT_PORTAL",
  // customer portal
  CP_DASHBOARD = "CP_DASHBOARD",
  CP_USERMANAGEMENT = "CP_USERMANAGEMENT",
  CP_PROJECTS = "CP_PROJECTS",
  CP_INVOICES = "CP_INVOICES",
  CP_WORKDOCKETS = "CP_WORKDOCKETS",
  CP_WASTEMANAGEMENT = "CP_WASTEMANAGEMENT",
  CP_WAREHOUSING = "CP_WAREHOUSING",
  CP_SUPPORT = "CP_SUPPORT",
  CP_WASTE_SUMMARY = "CP_WASTE_SUMMARY",
  CP_WASTE_REQUEST = "CP_WASTE_REQUEST",
}

export enum Action {
  READ = "READ",
  WRITE = "WRITE",
}

declare module "@tanstack/react-table" {
  /*
    * The additional properties that can be passed to the ColumnMeta
    {minWidth} - The minimum width of the column
    {className} - The class name of the column
    {classNameRow} - The class name of the row
    {showRightBorder} - Show right border of the column
    {data} - The data of the column
    {column} - The column definition
    {headerStyle} - The style of the header
    {minHeight} - The minimum height of the column
    { pin } – Boolean flag to indicate whether the column is pinned
    { pinDirection } – Specifies the pinning direction ("left" or "right")
  */
  interface ColumnMeta<TData, TValue> {
    minWidth?: number;
    className?: string;
    classNameRow?: string;
    showRightBorder?: boolean;
    showBottomBorder?: boolean;
    data?: TData;
    column?: ColumnDef<TData, TValue>;
    headerStyle?: React.CSSProperties;
    rosterContentMaxWidth?: number;
    showCustomBorder?: boolean;
    sortBy?: string;
    isColumnOrderable?: boolean;
    pin?: boolean;
    pinDirection?: "left" | "right";
  }
}

export type ExtendedColumnDef<T, V = unknown> = ColumnDef<T, V> & {
  idForRowClick?: keyof T;
  accessorKey?: DeepKeys<T> | string;
  map?: {
    key: string;
    valueFrom?: DeepKeys<T> | string;
    mergedValuesFrom?: (DeepKeys<T> | string)[];
    dataTransformer?: (data: T) => string;
    excludeFromExport?: boolean;
  };
  hideColumn?: boolean;
  sortBy?: string;
  isColumnOrderable?: boolean;
};

export enum EmployeeOptionsType {
  SKILLS = "SKILLS",
  TRAININGS = "TRAININGS",
}

export type _TTableFloatingMenuCustomComponentProps = Partial<{
  ids: string[];
  close: () => void;
  handleClose: () => void;
}>;

export type FloatingMenuCustomComponentProps = Partial<{
  ids: string[];
  close: () => void;
  handleClose: () => void;
}>;

export type _TFloatingMenuCustomComponentProps =
  FloatingMenuCustomComponentProps;

export type SocketNamespace = "" | "/";

export enum SocketNamespaces {
  EMPTY = "",
  BASE = "/",
}

export enum SOCKET_EVENT {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  ACCESS_UPDATE = "access-update",
  VERIFIED = "VERIFIED",
}

export type SocketData = {
  action: SOCKET_EVENT;
  userId: string;
};

export interface ProjectAssignedTool {
  id: string;
  createdAt: string;
  updatedAt: string;
  projectId: string;
  transferToolId: null;
  userId: string;
  subProductItemsList: null;
  note: string;
  description: string;
  quantity: number;
  status: string;
  returnStatus: string;
  remarks: null;
  returnRequestId: null;
  logs: ProductLogsItem[];
  comments: Comment[];
  assignedBy: AssignedBy;
  productAssignments: ProductAssignment[];
}
